"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

/** 与 public 目录下文件名一致（Materials for Plastic Injection Molding 文件夹） */
const DEFAULT_IMAGE_FILES = [
	"abs-molding.webp",
	"pa-molding.webp",
	"pbt-molding.webp",
	"pc-molding.webp",
	"peek-molding.webp",
	"pmma-molding.webp",
	"pom-molding.webp",
	"pp-molding.webp",
] as const;

/** 翻译 key 前缀，与 en.json 中 absTitle / absDesc 等一致 */
const MATERIAL_IDS = ["abs", "pa", "pbt", "pc", "peek", "pmma", "pom", "pp"] as const;

/** 单格：id 对应 `{id}Title` / `{id}Desc` / `{id}ImageAlt`，imageFile 为目录下文件名 */
export type MaterialsGridItem = { id: string; imageFile: string };

const PLASTIC_DEFAULT_GRID_ITEMS: MaterialsGridItem[] = MATERIAL_IDS.map((id, index) => ({
	id,
	imageFile: DEFAULT_IMAGE_FILES[index],
}));

export interface PlasticInjectionMaterialsGridProps {
	/** 翻译命名空间，见 `plasticInjectionMoldingMaterials` */
	translationNamespace?: string;
	/**
	 * 图片目录 URL 前缀（不含末尾斜杠），需对空格编码。
	 * 默认：`.../JS Plastic Injection Molding/Materials for Plastic Injection Molding`
	 */
	imageBaseUrl?: string;
	/** 底部按钮外链，默认上传页 */
	ctaHref?: string;
	/** 自定义条目与图片；不传则沿用注塑页默认 8 项（不影响其它页面） */
	items?: readonly MaterialsGridItem[];
}

/**
 * 材料/工艺网格：浅灰底、响应式多列（细线分割）、底部绿色 CTA。
 * 不传 `items` 时为注塑 8 材料；钣金页等可传 `items` + 独立 `translationNamespace`。
 */
export default function PlasticInjectionMaterialsGrid({
	translationNamespace = "plasticInjectionMoldingMaterials",
	imageBaseUrl = "/image/new-version/molding/plastic-injection-molding/materials",
	ctaHref = "/upload",
	items,
}: PlasticInjectionMaterialsGridProps) {
	const t = useTranslations(translationNamespace);
	const gridItems = items?.length ? items : PLASTIC_DEFAULT_GRID_ITEMS;

	return (
		<section className="w-full bg-[#f0f0f0] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("title")}
				</h2>
				<p className="mx-auto mb-6 text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("subtitle")}
				</p>

				{/*
				 * 与稿图一致的「表格」边框：
				 * - 外圈 1px 浅灰描边 + 轻微圆角
				 * - 内部用 gap-px + 与分割线同色底，单元格纯白，形成均匀细线（横竖等粗）
				 */}
				<div className="mx-auto mb-10 max-w-7xl rounded-[2px]">
					<div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-8">
						{gridItems.map((item) => (
							<div
								key={item.id}
								className="flex flex-col rounded-[2px] border border-[#9d9d9d] bg-[#F3F3F3] p-5 sm:p-6 md:p-7"
							>
								<h3 className="mb-4 text-left text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(`${item.id}Title`)}
								</h3>
								<div className="relative mx-auto mb-4 aspect-square w-full max-w-[280px] overflow-hidden bg-[#F3F3F3]">
									<Image
										src={`${imageBaseUrl}/${item.imageFile}`}
										alt={t(`${item.id}ImageAlt`)}
										fill
										className="object-cover"
										sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
									/>
								</div>
								<p className="flex-1 text-left text-sm leading-relaxed text-black md:text-base">
									{t(`${item.id}Desc`)}
								</p>
							</div>
						))}
					</div>
				</div>

				<div className="flex justify-center">
					<a
						href={ctaHref}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center justify-center rounded bg-themePrimary400 px-8 py-3 text-sm font-semibold text-white transition hover:bg-opacity-90 sm:px-10 sm:py-3.5 sm:text-base"
					>
						{t("ctaButton")}
					</a>
				</div>
			</div>
		</section>
	);
}
